import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useDragLayer, useDrop } from "react-dnd";
import moment from "moment-timezone";
import CurrentTime from "../CurrentTime";
import CreationPreview from "./CreationPreview";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useScrollContext } from "../../../hooks/context/useScrollContext";
import { useMenuContext } from "../../../hooks/context/useMenuContext";
import { useGridDates } from "../../../hooks/grid/useGridDates";
import { usePermissions } from "../../../hooks/usePermissions";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import { useCompany } from "../../../../../../utils/hooks/company";
import { usePreferences } from "../../../../../Settings/Configuration/hooks/usePreferences";
import { useIsTemplate } from "../../../hooks/useIsTemplate";
import { getResourceRowHeight } from "../../../helpers/grid/getResourceRowHeight";
import { getResourceIndexFromY } from "../../../helpers/grid/getResourceIndexFromY";
import { combineDateAndTime } from "../../../../../../utils/helpers/date";
import { BASE_ROW_HEIGHT } from "../../../constants/height";
import { SCHEDULE_PREFERENCE_KEYS } from "../../../constants/scheduleTypes";

const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const color = { bg: "#eff6ff", border: "#60a5fa" };

function Body({ children, currentTimeOffset, cellWidth }: any) {
    const { t } = useTranslation();
    const company = useCompany();
    const [creationData, setCreationData] = useState<any | null>(null);
    const creationDataRef = useRef<any | null>(null);
    creationDataRef.current = creationData;
    const [isDraggingToCreate, setIsDraggingToCreate] = useState(false);
    const [hoveredResourceId, setHoveredResourceId] = useState<string | null>(null);

    const {
        type,
        loading,
        resources,
        resourceLayouts,
        dispatch,
        unavailable
    } = useScheduleContext();
    const isTemplate = useIsTemplate();
    const { canCreate } = usePermissions();
    const unavailableDisplay = usePreferences(`${SCHEDULE_PREFERENCE_KEYS[type]}.unavailableDisplay`) ?? "block";

    const { gridDates, gridStartTime } = useGridDates();
    const [{ mode }] = useFilters();
    const { openMenu, closeMenu } = useMenuContext();
    const { scheduleGridRef, setScheduleGridRef, stopScrolling, startScrolling, handleGridScroll } = useScrollContext();

    /**
     * Helper to check if a specific timestamp for a resource is marked as unavailable
     */
    const isTimeUnavailable = useCallback((resourceId: string, time: moment.Moment) => {
        if (!unavailable || mode !== "day") return false;

        return unavailable.some((block: any) => {
            if (block.resourceId !== resourceId) return false;
            const start = moment(block.startDate);
            const end = moment(block.endDate);
            // Check if 'time' falls within the [start, end) range
            return time.isBetween(start, end, null, "[)");
        });
    }, [unavailable, mode]);

    const isDraggingEvent = useDragLayer((monitor) => monitor.isDragging());

    useEffect(() => {
        if (!isDraggingEvent) setHoveredResourceId(null);
    }, [isDraggingEvent]);

    const [_, drop] = useDrop(() => ({
        accept: ["EVENT", "UNASSIGNED_EVENT"],
        hover: (_, monitor) => {
            const gridEl = scheduleGridRef.current;
            const clientOffset = monitor.getClientOffset();
            if (!gridEl || !clientOffset) return;
            const gridRect = gridEl.getBoundingClientRect();

            if (unavailableDisplay === "available-hover" && mode === "day") {
                const y = clientOffset.y - gridRect.top + gridEl.scrollTop;
                const resourceIndex = getResourceIndexFromY(y, resources, resourceLayouts);
                setHoveredResourceId(resourceIndex === -1 ? null : (resources[resourceIndex]?.id ?? null));
            }

            if (!monitor.isOver({ shallow: true })) {
                stopScrolling();
                return;
            }

            const SCROLL_THRESHOLD = 50;
            const topOvershoot = gridRect.top + SCROLL_THRESHOLD - clientOffset.y;
            const bottomOvershoot = clientOffset.y - (gridRect.bottom - SCROLL_THRESHOLD);

            if (topOvershoot > 0) startScrolling("up", topOvershoot / SCROLL_THRESHOLD);
            else if (bottomOvershoot > 0) startScrolling("down", bottomOvershoot / SCROLL_THRESHOLD);
            else stopScrolling();
        },
        drop: () => {
            stopScrolling();
            setHoveredResourceId(null);
        },
    }), [scheduleGridRef, stopScrolling, startScrolling, unavailableDisplay, mode, resources, resourceLayouts]);

    const combineRefs = (node: HTMLDivElement | null) => {
        setScheduleGridRef(node);
        drop(node);
    };

    const handleMouseUp = useCallback(() => {
        stopScrolling();
        const creationData = creationDataRef.current;
        if (creationData) {
            const finalData = {
                id: "event_" + crypto.randomUUID(),
                ...creationData,
                isTemplate
            };
            if (finalData.endDate.isAfter(finalData.startDate)) {
                dispatch({
                    type: "create-event",
                    payload: {
                        ...finalData,
                        id: finalData.id,
                        date: !finalData?.isTemplate
                            ? moment(finalData.date).format("dddd").toLocaleLowerCase()
                            : finalData.startDate.toISOString(true),
                        resourceId: finalData.resourceId,
                        startDate: finalData.startDate.toISOString(),
                        endDate: finalData.endDate.toISOString(),
                    },
                });
                dispatch({
                    type: "selected-cell-or-event",
                    payload: { isCreating: true, ...finalData }
                });
            }
            setCreationData(null);
            setIsDraggingToCreate(false);
        }
    }, [dispatch, stopScrolling, isTemplate]);

    const handleMouseMove = useCallback((e: globalThis.MouseEvent) => {
        const creationData = creationDataRef.current;
        if (!creationData || !gridStartTime || cellWidth <= 0) return;

        const gridEl = scheduleGridRef.current;
        if (gridEl) {
            const gridRect = gridEl.getBoundingClientRect();
            const SCROLL_THRESHOLD = 50;
            const rightOvershoot = e.clientX - (gridRect.right - SCROLL_THRESHOLD);
            const leftOvershoot = gridRect.left + SCROLL_THRESHOLD - e.clientX;
            if (rightOvershoot > 0) startScrolling("right", rightOvershoot / SCROLL_THRESHOLD);
            else if (leftOvershoot > 0) startScrolling("left", leftOvershoot / SCROLL_THRESHOLD);
            else stopScrolling();
        }

        const scrollDelta = gridEl ? gridEl.scrollLeft - creationData.initialScrollLeft : 0;
        const totalPixelChange = (e.clientX - creationData.initialX) + scrollDelta;
        const minutesPerPixel = 60 / cellWidth;
        const durationDeltaMinutes = Math.round(totalPixelChange * minutesPerPixel);

        let newEndDate = creationData.startDate.clone().add(durationDeltaMinutes, "minutes");
        newEndDate.minute(Math.floor(newEndDate.minute() / 15) * 15).second(0).millisecond(0);

        // Clamp to end of day
        const dayEnd = creationData.startDate.clone().endOf("day");
        if (newEndDate.isAfter(dayEnd)) newEndDate = dayEnd;

        // Clamp based on unavailable blocks
        const resourceUnavailableBlocks = unavailable?.filter(
            (block: any) => block.resourceId === creationData.resourceId
        ) || [];

        for (const block of resourceUnavailableBlocks) {
            const blockStart = moment(block.startDate);
            const blockEnd = moment(block.endDate);

            // Prevent overlapping unavailable blocks
            if (newEndDate.isAfter(blockStart) && creationData.startDate.isBefore(blockEnd)) {
                newEndDate = moment.min(newEndDate, blockStart);
            }
        }

        // Ensure endDate is not before startDate
        if (newEndDate.isBefore(creationData.startDate)) {
            newEndDate = creationData.startDate.clone();
        }

        if (mode === "day" && canCreate && type !== "my-profile") {
            setCreationData((prev: any) => prev ? { ...prev, endDate: newEndDate } : null);
        }
    }, [gridStartTime, cellWidth, scheduleGridRef, unavailable, mode, canCreate, type, startScrolling, stopScrolling]);

    const handleGridMouseDown = (e: MouseEvent<HTMLDivElement>) => {
        if (loading) return;
        const target = e.target as HTMLElement;
        if (target.closest(".work-block") || target.closest(".unavailable-block")) return;

        e.preventDefault();
        const gridEl = e.currentTarget;
        const rect = gridEl.getBoundingClientRect();
        const y = e.clientY - rect.top + gridEl.scrollTop;
        const x = e.clientX - rect.left + gridEl.scrollLeft;

        if (!gridStartTime) return;

        const resourceIndex = getResourceIndexFromY(y, resources, resourceLayouts);
        if (resourceIndex === -1) return;
        const resource = resources[resourceIndex];
        if (!resource) return;

        const minutesPerPixel = 60 / cellWidth;
        const offsetMinutes = x * minutesPerPixel;
        let startDate = gridStartTime.clone().add(offsetMinutes, "minutes");
        startDate.minute(Math.floor(startDate.minute() / 15)).second(0).millisecond(0);

        // ✅ Guard: Block creation if starting point is unavailable
        if (isTimeUnavailable(resource.id, startDate)) return;

        if (mode === "day" && !(resource as any)?.type && canCreate && type !== "my-profile") {
            let rowTop = 0;
            for (let i = 0; i < resourceIndex; i++) {
                rowTop += getResourceRowHeight(resources[i].id, resourceLayouts);
            }
            const laneIndex = Math.floor((y - rowTop) / BASE_ROW_HEIGHT);

            setCreationData({
                resourceId: resource.id,
                user: resource,
                resourceIndex,
                laneIndex,
                startDate,
                date: startDate,
                endDate: startDate.clone(),
                initialX: e.clientX,
                initialScrollLeft: gridEl.scrollLeft,
                isTemplate
            });
            setIsDraggingToCreate(true);
        }
    };

    const handleGridDoubleClick = (e: MouseEvent<HTMLDivElement>) => {
        if (loading) return;
        stopScrolling();
        setIsDraggingToCreate(false);
        setCreationData(null);

        const target = e.target as HTMLElement;
        if (target.closest(".work-block") || target.closest(".unavailable-block")) return;

        const gridEl = e.currentTarget;
        const rect = gridEl.getBoundingClientRect();
        const y = e.clientY - rect.top + gridEl.scrollTop;
        const x = e.clientX - rect.left + gridEl.scrollLeft;

        if (!gridStartTime || !gridDates.length) return;

        const resourceIndex = getResourceIndexFromY(y, resources, resourceLayouts);
        if (resourceIndex === -1) return;
        const resource = resources[resourceIndex];

        const columnIndex = Math.floor(x / cellWidth);
        const clickedDate = gridDates[columnIndex];
        if (!clickedDate) return;

        const offsetXInColumn = x - (columnIndex * cellWidth);
        let startDate = moment(clickedDate).clone().add(offsetXInColumn * (60 / cellWidth), "minutes");
        startDate.minute(Math.floor(startDate.minute() / 15)).second(0).millisecond(0);

        // ✅ Guard: Block creation if double-clicked area is unavailable
        if (isTimeUnavailable(resource.id, startDate)) return;

        let endDate = startDate.clone().add(1, "hour");

        if (mode !== "day") {
            const calcGroup = (resource as any)?.calculationGroup;
            const scheduleSettings = calcGroup?.schedule || company?.settings?.schedule;
            const shiftStart = scheduleSettings?.shiftStart ? moment.parseZone(scheduleSettings.shiftStart) : moment().hour(9);
            const shiftEnd = scheduleSettings?.shiftEnd ? moment.parseZone(scheduleSettings.shiftEnd) : moment().hour(17);
            startDate = combineDateAndTime(moment(clickedDate), moment(shiftStart));
            endDate = combineDateAndTime(moment(clickedDate), moment(shiftEnd));
        }

        if (!(resource as any)?.type && canCreate && type !== "my-profile") {
            const finalData = {
                id: "event_" + crypto.randomUUID(),
                resourceId: resource.id,
                user: resource,
                startDate,
                endDate,
                date: moment(startDate).format("YYYY-MM-DD"),
                isTemplate,
            };
            dispatch({
                type: "create-event",
                payload: { ...finalData, startDate: finalData.startDate.toISOString(), endDate: finalData.endDate.toISOString() },
            });
            dispatch({ type: "selected-cell-or-event", payload: { isCreating: true, ...finalData } });
        }
    };

    const handleGridContextMenu = (e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "my-profile") return;
        if ((e.target as HTMLElement).closest(".work-block") || (e.target as HTMLElement).closest(".unavailable-block")) {
            closeMenu();
            return;
        }

        const gridEl = e.currentTarget;
        const rect = gridEl.getBoundingClientRect();
        const y = e.clientY - rect.top + gridEl.scrollTop;
        const x = e.clientX - rect.left + gridEl.scrollLeft;

        const resourceIndex = getResourceIndexFromY(y, resources, resourceLayouts);
        if (resourceIndex === -1) return;
        const resource = resources[resourceIndex];

        const columnIndex = Math.floor(x / cellWidth);
        const clickedDate = gridDates[columnIndex];
        if (!clickedDate || !gridStartTime) return;

        let clickedTime = moment(clickedDate).clone().add((x % cellWidth) * (60 / cellWidth), "minutes");
        clickedTime.minute(Math.floor(clickedTime.minute() / 15)).second(0).millisecond(0);

        // ✅ Guard: Don't open context menu if the time is unavailable
        if (isTimeUnavailable(resource.id, clickedTime)) return;

        if (resource && !(resource as any)?.type) {
            let startDate = clickedTime.clone();
            let endDate = clickedTime.clone().add(1, "hour");

            if (mode !== "day") {
                const scheduleSettings = (resource as any)?.calculationGroup?.schedule || company?.settings?.schedule;
                const shiftStart = scheduleSettings?.shiftStart ? moment.parseZone(scheduleSettings.shiftStart) : moment(clickedDate).hour(9);
                const shiftEnd = scheduleSettings?.shiftEnd ? moment.parseZone(scheduleSettings.shiftEnd) : moment(clickedDate).hour(17);
                startDate = combineDateAndTime(moment(clickedDate), moment(shiftStart));
                endDate = combineDateAndTime(moment(clickedDate), moment(shiftEnd));
            }

            openMenu({
                id: "cell-menu",
                x: e.clientX,
                y: e.clientY,
                resource,
                selected: {
                    id: "event_" + crypto.randomUUID(),
                    date: moment(startDate).format("YYYY-MM-DD"),
                    startDate: moment(startDate).toDate(),
                    endDate: moment(endDate).toDate(),
                    user: resource,
                    isTemplate
                }
            });
        }
    };

    // handleMouseMove/handleMouseUp read creationData from creationDataRef rather than
    // closing over the state directly, so their identity (and this effect) stays stable
    // for the whole drag instead of re-running on every position update - re-running here
    // used to tear down and restart the auto-scroll loop on nearly every mousemove event,
    // which made the auto-scroll feel stepwise/jerky instead of continuous.
    useEffect(() => {
        if (!isDraggingToCreate) return;
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp, { once: true });
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            stopScrolling();
        };
    }, [isDraggingToCreate, handleMouseMove, handleMouseUp, stopScrolling]);

    const creationPreviewTop = useMemo(() => {
        if (!creationData) return 0;
        let cumulativeHeight = 0;
        for (let i = 0; i < creationData.resourceIndex; i++) {
            cumulativeHeight += getResourceRowHeight(resources[i].id, resourceLayouts);
        }
        return cumulativeHeight;
    }, [creationData, resources, resourceLayouts]);

    const duration = useMemo(() => {
        if (!creationData) return 0;
        return moment(creationData.endDate).diff(moment(creationData.startDate), "minutes");
    }, [creationData]);

    const cumulativeRowTops = useMemo(() => {
        if (unavailableDisplay !== "available-hover") return {};
        let top = 0;
        return resources.reduce<Record<string, { top: number; height: number }>>((acc, resource) => {
            const height = getResourceRowHeight(resource.id, resourceLayouts);
            acc[resource.id] = { top, height };
            top += height;
            return acc;
        }, {});
    }, [resources, resourceLayouts, unavailableDisplay]);

    const allDayUnavailableRows = useMemo(() => {
        if (unavailableDisplay !== "available-hover" || !gridStartTime || cellWidth <= 0) return [];
        const dayStart = gridStartTime.clone().startOf("day");
        const totalWidth = gridDates.length * cellWidth;
        const seen = new Set<string>();
        const rows: { top: number; height: number }[] = [];
        ((unavailable ?? []) as any[]).forEach((b: any) => {
            if (!b.allDay || !moment(b.startDate).isSame(dayStart, "day")) return;
            if (seen.has(b.resourceId)) return;
            seen.add(b.resourceId);
            const meta = cumulativeRowTops[b.resourceId];
            if (meta) rows.push({ top: meta.top, height: meta.height });
        });
        return rows.map((r) => ({ ...r, width: totalWidth }));
    }, [unavailableDisplay, gridStartTime, cellWidth, gridDates, unavailable, cumulativeRowTops]);

    const availableZones = useMemo(() => {
        if (unavailableDisplay !== "available-hover" || !hoveredResourceId || !gridStartTime || cellWidth <= 0) return [];
        const dayStart = gridStartTime.clone().startOf("day");
        const dayEnd = dayStart.clone().endOf("day");
        const blocks = (unavailable ?? []).filter((b: any) =>
            b.resourceId === hoveredResourceId && moment(b.startDate).isSame(dayStart, "day")
        );
        if (!blocks.length) return [];

        const sorted = [...blocks].sort((a: any, b: any) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf());

        const zones: { left: number; width: number }[] = [];
        let cursor = dayStart.clone();

        for (const block of sorted) {
            const blockStart = moment(block.startDate);
            const blockEnd = moment(block.endDate);
            if (blockStart.isAfter(cursor)) {
                const leftPx = (cursor.diff(dayStart, "minutes") / 60) * cellWidth;
                const widthPx = (blockStart.diff(cursor, "minutes") / 60) * cellWidth;
                if (widthPx > 0) zones.push({ left: leftPx, width: widthPx });
            }
            if (blockEnd.isAfter(cursor)) cursor = blockEnd.clone();
        }

        if (cursor.isBefore(dayEnd)) {
            const leftPx = (cursor.diff(dayStart, "minutes") / 60) * cellWidth;
            const widthPx = (dayEnd.diff(cursor, "minutes") / 60) * cellWidth;
            if (widthPx > 0) zones.push({ left: leftPx, width: widthPx });
        }

        return zones;
    }, [unavailableDisplay, hoveredResourceId, gridStartTime, cellWidth, unavailable]);

    const unavailableZones = useMemo(() => {
        if (unavailableDisplay !== "available-hover" || !hoveredResourceId || !gridStartTime || cellWidth <= 0) return [];
        const dayStart = gridStartTime.clone().startOf("day");
        const blocks: any[] = ((unavailable ?? []) as any[]).filter((b: any) =>
            b.resourceId === hoveredResourceId && moment(b.startDate).isSame(dayStart, "day")
        );
        if (blocks.some((b: any) => b.allDay)) return [];
        const zones: { left: number; width: number }[] = [];
        for (const b of blocks) {
            const blockStart = moment.max(moment(b.startDate), dayStart);
            const blockEnd = moment(b.endDate);
            const leftPx = (blockStart.diff(dayStart, "minutes") / 60) * cellWidth;
            const widthPx = (blockEnd.diff(blockStart, "minutes") / 60) * cellWidth;
            if (widthPx > 0) zones.push({ left: leftPx, width: widthPx });
        }
        return zones;
    }, [unavailableDisplay, hoveredResourceId, gridStartTime, cellWidth, unavailable]);

    const handleGridHover = useCallback((e: MouseEvent<HTMLDivElement>) => {
        if (isDraggingToCreate) return;
        const gridEl = e.currentTarget;
        const rect = gridEl.getBoundingClientRect();
        const y = e.clientY - rect.top + gridEl.scrollTop;
        const resourceIndex = getResourceIndexFromY(y, resources, resourceLayouts);
        setHoveredResourceId(resourceIndex === -1 ? null : (resources[resourceIndex]?.id ?? null));
    }, [isDraggingToCreate, resources, resourceLayouts]);

    const rowMeta = hoveredResourceId ? cumulativeRowTops[hoveredResourceId] : null;

    return (
        <div
            ref={combineRefs}
            className="schedule-grid-container"
            onScroll={handleGridScroll}
            onMouseDown={handleGridMouseDown}
            onDoubleClick={(canCreate && type !== "my-profile") ? handleGridDoubleClick : undefined}
            onContextMenu={handleGridContextMenu}
            onMouseMove={unavailableDisplay === "available-hover" ? handleGridHover : undefined}
            onMouseLeave={unavailableDisplay === "available-hover" ? () => setHoveredResourceId(null) : undefined}
        >
            <div
                className="schedule-grid-content"
                style={{
                    position: "relative",
                    gridTemplateColumns: `repeat(${gridDates.length}, ${cellWidth}px)`,
                    gridTemplateRows: resources.map((r) => `${getResourceRowHeight(r.id, resourceLayouts)}px`).join(" "),
                }}
            >
                {children}
                {(unavailableDisplay === "available-hover" && mode === "day") && allDayUnavailableRows.map((row, i) => (
                    <div
                        key={`allday-${i}`}
                        className="tw-pointer-events-none tw-absolute tw-bg-neutral-100 tw-flex tw-flex-col tw-text-sm tw-items-center tw-justify-center"
                        style={{
                            top: `${row.top}px`,
                            height: `${row.height}px`,
                            left: 0,
                            width: `${row.width}px`,
                        }}
                    >
                        <p>
                            {t("unavailable")}
                        </p>
                        <p className="tw-font-semibold">
                            {t("all-day")}
                        </p>
                    </div>
                ))}
                {(unavailableDisplay === "available-hover" && rowMeta && mode === "day") && unavailableZones.map((zone, i) => (
                    <div
                        key={`unavailable-${i}`}
                        className="tw-pointer-events-none tw-absolute tw-bg-neutral-100"
                        style={{
                            top: `${rowMeta.top}px`,
                            height: `${rowMeta.height}px`,
                            left: `${zone.left}px`,
                            width: `${zone.width}px`,
                        }}
                    />
                ))}
                {(unavailableDisplay === "available-hover" && rowMeta && mode === "day") && availableZones.map((zone, i) => (
                    <div
                        key={i}
                        className="tw-pointer-events-none tw-absolute tw-flex tw-items-center tw-justify-center tw-overflow-hidden"
                        style={{
                            top: `${rowMeta.top}px`,
                            height: `${rowMeta.height}px`,
                            left: `${zone.left}px`,
                            width: `${zone.width}px`,
                            backgroundColor: hexToRgba(color.border, 0.1),
                            border: `1px dashed ${color.border}`,
                        }}
                    >
                        <span
                            className="tw-text-xs tw-font-medium tw-truncate tw-px-1 tw-select-none"
                            style={{ color: color.border }}
                        >
                            {t("available-time")}
                        </span>
                    </div>
                ))}
                {(creationData && gridStartTime && duration > 0 && mode === "day") && (
                    <CreationPreview
                        data={creationData}
                        rowTop={creationPreviewTop}
                        cellWidth={cellWidth}
                    />
                )}
                <CurrentTime currentTimeOffset={currentTimeOffset} cellWidth={cellWidth} />
            </div>
        </div>
    );
}

export default Body;
